import {
    Customer,
    EN16931_TYPE_CODE,
    Invoice,
    InvoiceCompanySnapshot,
    InvoiceConfig,
    InvoiceLine,
    TaxGroup,
} from '../../types';
import { Cents, taxFromNet } from '../money';
import {
    BankAccountInfo,
    CUSTOMIZATION_ID,
    DOCUMENT_CURRENCY_CODE,
    PostalAddress,
    SellerContact,
    en16931CumulativeRetentionCents,
    en16931PayableCents,
    en16931PrepaidCents,
    formatSkontoTerms,
    isXmlInvoiceLine,
    lineTaxCategory,
    defaultTaxCategoryCode,
    readOptionalFlag,
    readOptionalRecord,
    readOptionalText,
    resolveBankAccount,
    resolveBuyerAddress,
    resolveBuyerReference,
    resolvePaymentMeansCode,
    resolveSellerAddress,
    resolveSellerContact,
} from './ublInvoice';

/**
 * Geschaeftsregeln der EN 16931 und der deutschen Auspraegung XRechnung, so weit
 * sie sich am Datenmodell dieses Moduls ueberhaupt verletzen lassen.
 *
 * Warum eigene Regeln, obwohl es den KoSIT-Validator gibt: der KoSIT-Validator
 * laeuft gegen fertiges XML, also erst NACH dem Ausstellen — und ausgestellte
 * Belege sind festgeschrieben. Ein Mangel, der dort auffaellt, ist nur noch
 * ueber Storno und Neuausstellung zu heilen. Diese Regeln greifen davor, auf
 * dem Entwurf, und liefern deutsche Meldungen statt Schematron-Ausgaben.
 *
 * Die Regeln sind pure Praedikate ueber einem vorberechneten Kontext. Kein
 * Supabase, kein Date.now(), keine Reihenfolgeabhaengigkeit — jede Regel laesst
 * sich einzeln testen, und die Reihenfolge in EN16931_RULES bestimmt nur, in
 * welcher Reihenfolge die Meldungen erscheinen.
 *
 * NICHT umgesetzt und warum:
 *  - BR-CO-11 und BR-CO-12 (Summe der Nachlaesse bzw. Zuschlaege auf
 *    Dokumentebene): das Modell kennt keine Dokumentebene fuer Nachlaesse.
 *    DISCOUNT und SURCHARGE sind Positionen, BT-107 und BT-108 werden also nie
 *    geschrieben. Eine Regel darauf koennte nie feuern und waere eine Attrappe.
 *  - Die Kategorien Z, E, AE, K, G und O haben eigene Regelsaetze (BR-Z-*,
 *    BR-E-* usw.). Umgesetzt ist hier BR-S-*, weil die Regelbesteuerung der
 *    Normalfall ist; die Nullsatz-Faelle deckt vorerst validateInvoice ab.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Ergebnis- und Regeltypen
// ═══════════════════════════════════════════════════════════════════════════

export type RuleSeverity = 'error' | 'warning';

export interface RuleViolation {
    /** Regelkennung der EN 16931 bzw. der XRechnung, z.B. "BR-DE-15". */
    code: string;
    severity: RuleSeverity;
    /** Feldpfad im Modell, damit die Oberflaeche direkt dorthin springen kann. */
    field?: string;
    message: string;
}

/**
 * Vorberechneter Auszug aus dem Beleg.
 *
 * Adressen, Kontakt und Bankverbindung werden EINMAL aufgeloest und an alle
 * Regeln gereicht. Das ist nicht nur schneller: die Regeln pruefen damit
 * garantiert denselben Wert, den der Generator spaeter ins XML schreibt, statt
 * jede fuer sich eine eigene Rueckfallkette zu bauen.
 */
export interface RuleContext {
    invoice: Invoice;
    customer?: Customer;
    company?: InvoiceCompanySnapshot;
    config?: InvoiceConfig;
    /** Nur die Zeilen, die tatsaechlich zu einer cac:InvoiceLine werden. */
    lines: InvoiceLine[];
    taxGroups: TaxGroup[];
    sellerAddress: PostalAddress;
    buyerAddress: PostalAddress;
    sellerContact: SellerContact;
    bank: BankAccountInfo;
    sellerVatId?: string;
    sellerTaxNumber?: string;
    buyerVatId?: string;
    buyerReference?: string;
    /** Nur die ausdrueckliche Leitweg-ID, ohne den Rueckfall auf die Kundennummer. */
    leitwegId?: string;
    paymentMeansCode: string;
    typeCode?: string;
}

export interface En16931Rule {
    code: string;
    severity: RuleSeverity;
    field?: string;
    /** Deutsche Meldung; als Funktion, wo der Mangel beziffert werden kann. */
    message: string | ((ctx: RuleContext) => string);
    /** Pures Praedikat. true bedeutet: Regel verletzt. */
    isViolated: (ctx: RuleContext) => boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hilfsmittel
// ═══════════════════════════════════════════════════════════════════════════

const isAmount = (value: unknown): value is Cents =>
    typeof value === 'number' && Number.isFinite(value);

const ISO_COUNTRY_CODE = /^[A-Z]{2}$/;
const ISO_CURRENCY_CODE = /^[A-Z]{3}$/;

/**
 * USt-IdNr. mit Laenderpraefix (BR-CO-9).
 *
 * Nur das Praefix wird geprueft, nicht die Pruefziffer: die Regeln je Land sind
 * unterschiedlich, und eine falsch abgelehnte gueltige Nummer waere schlimmer
 * als eine durchgelassene falsche — die faengt spaetestens das Finanzamt.
 */
const VAT_ID_WITH_COUNTRY_PREFIX = /^[A-Z]{2}[0-9A-Z+*.]{2,}$/;

/** Leitweg-ID: Grobadressierung, optionale Feinadressierung, zweistellige Pruefziffer. */
const LEITWEG_ID_PATTERN = /^\d{2,12}(-[0-9A-Za-z]{1,30})?-\d{2}$/;

/**
 * Namensbestandteile, die auf einen oeffentlichen Auftraggeber hindeuten.
 *
 * Bewusst nur ein Verdacht und deshalb nur eine Warnung: es gibt die
 * "Stadtreinigung GmbH" ohne Leitweg-ID genauso wie das Amt, das eine braucht.
 * Wer es sicher wissen will, setzt das Kennzeichen am Kunden.
 */
const PUBLIC_AUTHORITY_HINT =
    /(^|\s)(stadt|gemeinde|landkreis|kreisverwaltung|bezirksamt|amt|beh(ö|oe)rde|ministerium|bundesamt|landesamt|universit(ä|ae)t|hochschule|klinikum|zweckverband|eigenbetrieb)(\s|$)/i;

/** Zulaessige Belegarten der XRechnung (BR-DE-17). */
const ALLOWED_TYPE_CODES = new Set(['326', '380', '381', '384', '389', '875', '876', '877']);

const sumCentsOf = (values: Array<number | undefined>): Cents =>
    values.reduce<number>((acc, value) => acc + (isAmount(value) ? value : 0), 0);

const hasAnyAddressPart = (address: PostalAddress): boolean =>
    !!address.street || !!address.postalZone || !!address.cityName;

/** Steuergruppen der Regelbesteuerung — Bezugspunkt fast aller BR-S-Regeln. */
const standardRatedGroups = (ctx: RuleContext): TaxGroup[] =>
    // Der Rueckfall muss derselbe sein, den die XML-Erzeugung benutzt:
    // satzabhaengig, nicht pauschal 'S'. Sonst gilt eine 0-%-Gruppe ohne
    // ausdrueckliche Kategorie fuer den Generator als Z und fuer die Regeln als
    // S — BR-S-8 vergleicht dann eine Steuersumme von 0 gegen eine
    // Bemessungsgrundlage von 100.000, und BR-S-10 verbietet den
    // Befreiungsgrund, der bei § 13b oder § 19 gerade Pflicht ist. Der Beleg
    // liesse sich mit zwei Fehlern nicht ausstellen, die beide in die falsche
    // Richtung weisen. Erreichbar ueberall dort, wo totals als jsonb aus der
    // Datenbank kommt statt frisch aus calculateInvoiceTotals.
    ctx.taxGroups.filter(
        (group) => (group.taxCategoryCode ?? defaultTaxCategoryCode(group.taxRateBp ?? 0)) === 'S',
    );

const standardRatedLines = (ctx: RuleContext): InvoiceLine[] =>
    ctx.lines.filter((line) => lineTaxCategory(line) === 'S');

/**
 * Waehrungskennung des Belegs.
 *
 * Das Modul rechnet ausschliesslich in Euro-Cent (siehe money.ts). Die
 * Konfiguration kann das nicht umstellen — wenn dort dennoch etwas anderes
 * steht, ist das ein Konfigurationsfehler und keine zweite Waehrung.
 */
const resolveCurrencyCode = (config?: InvoiceConfig): string =>
    (readOptionalText(config, 'currency') ?? DOCUMENT_CURRENCY_CODE).toUpperCase();

// ═══════════════════════════════════════════════════════════════════════════
// Kontext
// ═══════════════════════════════════════════════════════════════════════════

export const buildRuleContext = (
    invoice: Invoice,
    customer: Customer | undefined,
    company: InvoiceCompanySnapshot | undefined,
    config?: InvoiceConfig,
): RuleContext => ({
    invoice,
    customer,
    company,
    config,
    lines: (invoice?.lines ?? []).filter(isXmlInvoiceLine),
    taxGroups: invoice?.totals?.taxGroups ?? [],
    sellerAddress: resolveSellerAddress(company),
    buyerAddress: resolveBuyerAddress(customer),
    sellerContact: resolveSellerContact(company, config),
    bank: resolveBankAccount(invoice, company, config),
    sellerVatId: company?.vatId?.trim() || config?.vatId?.trim() || undefined,
    sellerTaxNumber: company?.taxNumber?.trim() || config?.taxNumber?.trim() || undefined,
    buyerVatId: readOptionalText(customer, 'vatId'),
    buyerReference: resolveBuyerReference(invoice, customer),
    leitwegId:
        readOptionalText(invoice?.details, 'leitwegId') ??
        readOptionalText(customer, 'leitwegId') ??
        invoice?.details?.buyerReference?.trim() ??
        undefined,
    paymentMeansCode: resolvePaymentMeansCode(invoice),
    typeCode: EN16931_TYPE_CODE[invoice?.documentType],
});

// ═══════════════════════════════════════════════════════════════════════════
// BR-1 bis BR-17 — Pflichtangaben des Kerndatenmodells
// ═══════════════════════════════════════════════════════════════════════════

const CORE_RULES: En16931Rule[] = [
    {
        code: 'BR-1',
        severity: 'error',
        // Kann aus Belegdaten nicht feuern: BT-24 ist eine Konstante des
        // Generators. Die Regel bleibt trotzdem im Satz, damit ein spaeterer
        // Wechsel der Syntax (CII statt UBL) nicht unbemerkt die Kennung
        // leert — dann meldet sie sofort statt erst beim Empfaenger.
        message: 'Es fehlt die Spezifikationskennung (BT-24) der XRechnung.',
        isViolated: () => !CUSTOMIZATION_ID.trim(),
    },
    {
        code: 'BR-2',
        severity: 'error',
        field: 'invoiceNumber',
        message: 'Die Rechnung hat keine Rechnungsnummer (BT-1).',
        isViolated: (ctx) => !ctx.invoice?.invoiceNumber?.trim(),
    },
    {
        code: 'BR-3',
        severity: 'error',
        field: 'invoiceDate',
        message: 'Es fehlt das Rechnungsdatum (BT-2).',
        isViolated: (ctx) => !ctx.invoice?.invoiceDate?.trim(),
    },
    {
        code: 'BR-4',
        severity: 'error',
        field: 'documentType',
        message: 'Der Belegart ist kein Rechnungstyp (BT-3) zugeordnet.',
        isViolated: (ctx) => !ctx.typeCode,
    },
    {
        code: 'BR-5',
        severity: 'error',
        message:
            'Die Belegwährung (BT-5) ist kein gültiger ISO-4217-Code. Dieses Modul rechnet ausschließlich in Euro.',
        isViolated: (ctx) => {
            const currency = resolveCurrencyCode(ctx.config);
            return !ISO_CURRENCY_CODE.test(currency) || currency !== DOCUMENT_CURRENCY_CODE;
        },
    },
    {
        code: 'BR-6',
        severity: 'error',
        message: 'Es fehlt der Name des Rechnungsstellers (BT-27).',
        isViolated: (ctx) => !ctx.company?.name?.trim(),
    },
    {
        code: 'BR-7',
        severity: 'error',
        field: 'customerId',
        message: 'Es fehlt der Name des Rechnungsempfängers (BT-44).',
        isViolated: (ctx) => !ctx.customer?.name?.trim(),
    },
    {
        code: 'BR-8',
        severity: 'error',
        message: 'Es fehlt die Postanschrift des Rechnungsstellers (BG-5).',
        isViolated: (ctx) => !hasAnyAddressPart(ctx.sellerAddress),
    },
    {
        code: 'BR-9',
        severity: 'error',
        message: 'Der Ländercode des Rechnungsstellers (BT-40) ist kein ISO-3166-1-alpha-2-Code.',
        isViolated: (ctx) => !ISO_COUNTRY_CODE.test(ctx.sellerAddress.countryCode),
    },
    {
        code: 'BR-10',
        severity: 'error',
        field: 'customerId',
        message: 'Es fehlt die Postanschrift des Rechnungsempfängers (BG-8).',
        isViolated: (ctx) => !hasAnyAddressPart(ctx.buyerAddress),
    },
    {
        code: 'BR-11',
        severity: 'error',
        field: 'customerId',
        message: 'Der Ländercode des Rechnungsempfängers (BT-55) ist kein ISO-3166-1-alpha-2-Code.',
        isViolated: (ctx) => !ISO_COUNTRY_CODE.test(ctx.buyerAddress.countryCode),
    },
    {
        code: 'BR-12',
        severity: 'error',
        field: 'lines',
        message:
            'Die Summe der Positionsnettobeträge (BT-106) lässt sich nicht bilden: mindestens eine Position hat keinen Nettobetrag.',
        isViolated: (ctx) => ctx.lines.some((line) => !isAmount(line.netCents)),
    },
    {
        code: 'BR-13',
        severity: 'error',
        field: 'totals',
        message: 'Es fehlt der Gesamtbetrag ohne Umsatzsteuer (BT-109).',
        isViolated: (ctx) => !isAmount(ctx.invoice?.totals?.netTotalCents),
    },
    {
        code: 'BR-14',
        severity: 'error',
        field: 'totals',
        message: 'Es fehlt der Gesamtbetrag einschließlich Umsatzsteuer (BT-112).',
        isViolated: (ctx) => !isAmount(ctx.invoice?.totals?.grossTotalCents),
    },
    {
        code: 'BR-15',
        severity: 'error',
        field: 'totals',
        message: 'Es fehlt der fällige Zahlbetrag (BT-115).',
        isViolated: (ctx) => !isAmount(ctx.invoice?.totals?.payableCents),
    },
    {
        code: 'BR-16',
        severity: 'error',
        field: 'lines',
        message:
            'Die Rechnung enthält keine abrechenbare Position (BG-25). Abschnitte und Texte zählen nicht als Position.',
        isViolated: (ctx) => ctx.lines.length === 0,
    },
    {
        code: 'BR-17',
        severity: 'error',
        field: 'details.bankAccount',
        // Ein am Beleg hinterlegtes, vom Firmenkonto abweichendes Konto macht
        // den Zahlungsempfaenger zu einem anderen als den Rechnungssteller.
        // Dann verlangt BT-59 einen Namen, sonst weiss der Kunde nicht, an wen
        // er zahlt — und die Zahlung landet im Zweifel gar nicht.
        message: 'Das abweichende Zahlungskonto hat keinen Kontoinhaber (BT-59).',
        isViolated: (ctx) => {
            const declaredIban = ctx.invoice?.details?.bankAccount?.iban?.trim();
            if (!declaredIban) return false;
            const sellerIban = ctx.company?.iban?.trim() || ctx.config?.iban?.trim();
            if (declaredIban === sellerIban) return false;
            return !ctx.invoice?.details?.bankAccount?.holder?.trim();
        },
    },
];

// ═══════════════════════════════════════════════════════════════════════════
// BR-CO-9 bis BR-CO-17 — Arithmetik und Konsistenz der Umsatzsteuer
// ═══════════════════════════════════════════════════════════════════════════

const ARITHMETIC_RULES: En16931Rule[] = [
    {
        code: 'BR-CO-9',
        severity: 'error',
        message:
            'Eine Umsatzsteuer-Identifikationsnummer beginnt nicht mit einem gültigen Länderpräfix (z. B. "DE").',
        isViolated: (ctx) =>
            [ctx.sellerVatId, ctx.buyerVatId]
                .filter((id): id is string => !!id)
                .some((id) => !VAT_ID_WITH_COUNTRY_PREFIX.test(id.replace(/\s/g, '').toUpperCase())),
    },
    {
        code: 'BR-CO-10',
        severity: 'error',
        field: 'totals',
        message: (ctx) =>
            `Die Summe der Positionsnettobeträge (BT-106) beträgt ${sumCentsOf(
                ctx.lines.map((line) => line.netCents),
            )} Cent, ausgewiesen sind ${ctx.invoice?.totals?.netTotalCents} Cent.`,
        isViolated: (ctx) => {
            const total = ctx.invoice?.totals?.netTotalCents;
            if (!isAmount(total)) return false;
            return sumCentsOf(ctx.lines.map((line) => line.netCents)) !== total;
        },
    },
    {
        code: 'BR-CO-13',
        severity: 'error',
        field: 'totals',
        // In diesem Modell entsteht der Gesamtbetrag ohne Umsatzsteuer
        // ausschliesslich aus den Bemessungsgrundlagen der Steuergruppen —
        // Nachlaesse und Zuschlaege auf Dokumentebene gibt es nicht.
        message:
            'Der Gesamtbetrag ohne Umsatzsteuer (BT-109) stimmt nicht mit der Summe der Bemessungsgrundlagen überein.',
        isViolated: (ctx) => {
            const total = ctx.invoice?.totals?.netTotalCents;
            if (!isAmount(total)) return false;
            return sumCentsOf(ctx.taxGroups.map((group) => group.taxableNetCents)) !== total;
        },
    },
    {
        code: 'BR-CO-14',
        severity: 'error',
        field: 'totals',
        message:
            'Der Gesamtbetrag der Umsatzsteuer (BT-110) stimmt nicht mit der Summe der Steuerbeträge je Steuersatz überein.',
        isViolated: (ctx) => {
            const total = ctx.invoice?.totals?.taxTotalCents;
            if (!isAmount(total)) return false;
            return sumCentsOf(ctx.taxGroups.map((group) => group.taxCents)) !== total;
        },
    },
    {
        code: 'BR-CO-15',
        severity: 'error',
        field: 'totals',
        message:
            'Der Bruttobetrag (BT-112) entspricht nicht dem Nettobetrag (BT-109) zuzüglich der Umsatzsteuer (BT-110).',
        isViolated: (ctx) => {
            const totals = ctx.invoice?.totals;
            if (
                !isAmount(totals?.netTotalCents) ||
                !isAmount(totals?.taxTotalCents) ||
                !isAmount(totals?.grossTotalCents)
            ) {
                return false;
            }
            return totals.netTotalCents + totals.taxTotalCents !== totals.grossTotalCents;
        },
    },
    {
        code: 'BR-CO-16',
        severity: 'error',
        field: 'totals',
        message:
            'Der fällige Zahlbetrag (BT-115) entspricht nicht dem Bruttobetrag (BT-112) abzüglich der erhaltenen Abschlagszahlungen (BT-113); ein Sicherheitseinbehalt mindert nur den Zahlbetrag des Belegs, nicht BT-115.',
        // Kumulatives Modell (docs/sicherheitseinbehalt-plan.md §16.3): das
        // XML nennt in BT-113 die tatsaechlich erhaltenen Abschlagszahlungen
        // (prepaid − prior) und in BT-115 Brutto minus BT-113, also den
        // Zahlbetrag PLUS den kumulierten Einbehalt (ublInvoice). Geprueft
        // werden deshalb drei Gleichungen:
        //   gross − BT-113 = BT-115                          (die Regel selbst)
        //   BT-115 = payable + cumulative                    (XML zum Zahlbetrag)
        //   payable = gross − prepaid − retention + release  (Zahlbetrag zu den Feldern)
        // Die dritte haelt den Zahlbetrag an den Einbehalt-Feldern fest: ein
        // Einbehalt oder eine Freigabe, die den Zahlbetrag nicht bewegt, liesse
        // sich sonst am Beleg vorbei eintragen. Bei Belegen ohne Einbehalt
        // (prior 0, cumulative 0) ist das dieselbe Gleichung wie bisher:
        // gross − prepaid = payable.
        isViolated: (ctx) => {
            const totals = ctx.invoice?.totals;
            if (!isAmount(totals?.grossTotalCents) || !isAmount(totals?.payableCents)) return false;
            const dueCents = en16931PayableCents(totals);
            if (totals.grossTotalCents - en16931PrepaidCents(totals) !== dueCents) return true;
            if (dueCents !== totals.payableCents + en16931CumulativeRetentionCents(totals)) return true;
            const prepaid = isAmount(totals.prepaidCents) ? totals.prepaidCents : 0;
            const retention = isAmount(totals.retentionCents) ? totals.retentionCents : 0;
            const release = isAmount(totals.retentionReleaseCents) ? totals.retentionReleaseCents : 0;
            return totals.payableCents !== totals.grossTotalCents - prepaid - retention + release;
        },
    },
    {
        code: 'BR-CO-17',
        severity: 'error',
        field: 'totals.taxGroups',
        message: (ctx) => {
            const offending = ctx.taxGroups.find(
                (group) => taxFromNet(group.taxableNetCents ?? 0, group.taxRateBp ?? 0) !== group.taxCents,
            );
            return `Der Steuerbetrag der Gruppe ${(offending?.taxRateBp ?? 0) / 100} % ergibt sich nicht aus Bemessungsgrundlage mal Steuersatz (BT-117).`;
        },
        isViolated: (ctx) =>
            ctx.taxGroups.some((group) => {
                if (!isAmount(group.taxableNetCents) || !isAmount(group.taxCents)) return false;
                return taxFromNet(group.taxableNetCents, group.taxRateBp ?? 0) !== group.taxCents;
            }),
    },
];

// ═══════════════════════════════════════════════════════════════════════════
// BR-S-* — Regelbesteuerung
// ═══════════════════════════════════════════════════════════════════════════

const STANDARD_RATE_RULES: En16931Rule[] = [
    {
        code: 'BR-S-1',
        severity: 'error',
        field: 'totals.taxGroups',
        message:
            'Es gibt Positionen mit Regelbesteuerung, aber keine passende Zeile in der Umsatzsteueraufschlüsselung.',
        isViolated: (ctx) => standardRatedLines(ctx).length > 0 && standardRatedGroups(ctx).length === 0,
    },
    {
        code: 'BR-S-2',
        severity: 'error',
        message:
            'Bei Regelbesteuerung muss die Umsatzsteuer-Identifikationsnummer oder die Steuernummer des Rechnungsstellers angegeben sein.',
        isViolated: (ctx) =>
            standardRatedLines(ctx).length > 0 && !ctx.sellerVatId && !ctx.sellerTaxNumber,
    },
    {
        code: 'BR-S-5',
        severity: 'error',
        field: 'lines',
        message: 'Eine Position mit Regelbesteuerung hat einen Steuersatz von 0 %.',
        isViolated: (ctx) => standardRatedLines(ctx).some((line) => (line.taxRateBp ?? 0) <= 0),
    },
    {
        code: 'BR-S-8',
        severity: 'error',
        field: 'totals.taxGroups',
        message: (ctx) => {
            const offending = standardRatedGroups(ctx).find(
                (group) =>
                    sumCentsOf(
                        standardRatedLines(ctx)
                            .filter((line) => (line.taxRateBp ?? 0) === (group.taxRateBp ?? 0))
                            .map((line) => line.netCents),
                    ) !== group.taxableNetCents,
            );
            return `Die Bemessungsgrundlage zum Steuersatz ${(offending?.taxRateBp ?? 0) / 100} % entspricht nicht der Summe der zugehörigen Positionen (BT-116).`;
        },
        isViolated: (ctx) =>
            standardRatedGroups(ctx).some((group) => {
                if (!isAmount(group.taxableNetCents)) return false;
                const linesOfRate = standardRatedLines(ctx).filter(
                    (line) => (line.taxRateBp ?? 0) === (group.taxRateBp ?? 0),
                );
                return sumCentsOf(linesOfRate.map((line) => line.netCents)) !== group.taxableNetCents;
            }),
    },
    {
        code: 'BR-S-9',
        severity: 'error',
        field: 'totals.taxGroups',
        message:
            'Der ausgewiesene Steuerbetrag einer regelbesteuerten Gruppe entspricht nicht Bemessungsgrundlage mal Steuersatz.',
        isViolated: (ctx) =>
            standardRatedGroups(ctx).some((group) => {
                if (!isAmount(group.taxableNetCents) || !isAmount(group.taxCents)) return false;
                return taxFromNet(group.taxableNetCents, group.taxRateBp ?? 0) !== group.taxCents;
            }),
    },
    {
        code: 'BR-S-10',
        severity: 'error',
        field: 'totals.taxGroups',
        // Ein Befreiungsgrund an einer regelbesteuerten Gruppe ist kein
        // Schoenheitsfehler: der Empfaenger liest daraus, die Position sei
        // steuerfrei, und zieht die ausgewiesene Vorsteuer nicht.
        message:
            'Eine regelbesteuerte Gruppe darf keinen Grund für eine Steuerbefreiung tragen (BT-120).',
        isViolated: (ctx) =>
            standardRatedGroups(ctx).some((group) => !!group.taxExemptionReason?.trim()),
    },
];

// ═══════════════════════════════════════════════════════════════════════════
// BR-DE-1 bis BR-DE-18 — deutsche Auspraegung (XRechnung)
// ═══════════════════════════════════════════════════════════════════════════

const GERMAN_RULES: En16931Rule[] = [
    {
        code: 'BR-DE-1',
        severity: 'error',
        field: 'details.bankAccount',
        message:
            'Es fehlen Zahlungsangaben (BG-16): weder eine Bankverbindung noch Zahlungsbedingungen sind hinterlegt.',
        isViolated: (ctx) =>
            !ctx.bank.iban &&
            !ctx.invoice?.details?.paymentTerms?.trim() &&
            !ctx.config?.defaultPaymentTerms?.trim(),
    },
    {
        code: 'BR-DE-2',
        severity: 'error',
        message: 'Es fehlen die Kontaktangaben des Rechnungsstellers (BG-6).',
        isViolated: (ctx) =>
            !ctx.sellerContact.name && !ctx.sellerContact.phone && !ctx.sellerContact.email,
    },
    {
        code: 'BR-DE-3',
        severity: 'error',
        message: 'Es fehlt der Ort des Rechnungsstellers (BT-37).',
        isViolated: (ctx) => !ctx.sellerAddress.cityName,
    },
    {
        code: 'BR-DE-4',
        severity: 'error',
        message: 'Es fehlt die Postleitzahl des Rechnungsstellers (BT-38).',
        isViolated: (ctx) => !ctx.sellerAddress.postalZone,
    },
    {
        code: 'BR-DE-5',
        severity: 'error',
        message: 'Es fehlt der Ansprechpartner des Rechnungsstellers (BT-41).',
        isViolated: (ctx) => !ctx.sellerContact.name,
    },
    {
        code: 'BR-DE-6',
        severity: 'error',
        message: 'Es fehlt die Telefonnummer des Rechnungsstellers (BT-42).',
        isViolated: (ctx) => !ctx.sellerContact.phone,
    },
    {
        code: 'BR-DE-7',
        severity: 'error',
        message: 'Es fehlt die E-Mail-Adresse des Rechnungsstellers (BT-43).',
        isViolated: (ctx) => !ctx.sellerContact.email,
    },
    {
        code: 'BR-DE-8',
        severity: 'error',
        field: 'customerId',
        message: 'Es fehlt der Ort des Rechnungsempfängers (BT-52).',
        isViolated: (ctx) => !ctx.buyerAddress.cityName,
    },
    {
        code: 'BR-DE-9',
        severity: 'error',
        field: 'customerId',
        message: 'Es fehlt die Postleitzahl des Rechnungsempfängers (BT-53).',
        isViolated: (ctx) => !ctx.buyerAddress.postalZone,
    },
    {
        code: 'BR-DE-10',
        severity: 'error',
        field: 'details.deliveryAddress',
        message: 'Die angegebene Lieferanschrift hat keinen Ort (BT-77).',
        isViolated: (ctx) => {
            const delivery = readOptionalRecord(ctx.invoice?.details, 'deliveryAddress');
            return !!delivery && !readOptionalText(delivery, 'city');
        },
    },
    {
        code: 'BR-DE-11',
        severity: 'error',
        field: 'details.deliveryAddress',
        message: 'Die angegebene Lieferanschrift hat keine Postleitzahl (BT-78).',
        isViolated: (ctx) => {
            const delivery = readOptionalRecord(ctx.invoice?.details, 'deliveryAddress');
            return !!delivery && !readOptionalText(delivery, 'zip');
        },
    },
    {
        code: 'BR-DE-12',
        severity: 'error',
        field: 'details.bankAccount',
        message: 'Für eine SEPA-Überweisung fehlt die IBAN des Zahlungsempfängers (BG-17).',
        isViolated: (ctx) =>
            (ctx.paymentMeansCode === '58' || ctx.paymentMeansCode === '59') && !ctx.bank.iban,
    },
    {
        code: 'BR-DE-13',
        severity: 'error',
        field: 'details.bankAccount',
        // Feuert erst, wenn am Beleg ausdruecklich Lastschrift steht. Der
        // Generator schreibt sonst 58; das Feld ist der vorgesehene Weg dorthin.
        message: 'Für eine SEPA-Lastschrift fehlen Mandatsreferenz oder belastetes Konto (BG-19).',
        isViolated: (ctx) => {
            if (ctx.paymentMeansCode !== '59') return false;
            const mandate = readOptionalText(ctx.invoice?.details, 'mandateReference');
            return !mandate || !ctx.bank.iban;
        },
    },
    {
        code: 'BR-DE-14',
        severity: 'error',
        field: 'details.paymentCard',
        message: 'Für eine Kartenzahlung fehlen die Karteninformationen (BG-18).',
        isViolated: (ctx) =>
            ctx.paymentMeansCode === '48' &&
            !readOptionalRecord(ctx.invoice?.details, 'paymentCard'),
    },
    {
        code: 'BR-DE-15',
        severity: 'error',
        field: 'details.buyerReference',
        message: 'Es fehlt die Käuferreferenz (BT-10). Ohne sie nimmt kein Empfänger die XRechnung an.',
        isViolated: (ctx) => !ctx.buyerReference,
    },
    {
        code: 'BR-DE-16',
        severity: 'error',
        message:
            'Es fehlt die Umsatzsteuer-Identifikationsnummer oder die Steuernummer des Rechnungsstellers (BT-31/BT-32).',
        isViolated: (ctx) => !ctx.sellerVatId && !ctx.sellerTaxNumber,
    },
    {
        code: 'BR-DE-17',
        severity: 'error',
        field: 'documentType',
        message: (ctx) => `Der Rechnungstyp ${ctx.typeCode ?? '(leer)'} ist in der XRechnung nicht zulässig.`,
        isViolated: (ctx) => !ctx.typeCode || !ALLOWED_TYPE_CODES.has(ctx.typeCode),
    },
    {
        code: 'BR-DE-18',
        severity: 'error',
        field: 'details.skonto',
        // Die XRechnung liest Skonto nicht aus dem Fliesstext, sondern aus dem
        // Muster #SKONTO#TAGE=..#PROZENT=..# in BT-20. Laesst es sich aus den
        // erfassten Werten nicht bilden (0 %, gebrochene Tage, NaN aus dem
        // Formular), zieht der Empfaenger den Abzug nicht und mahnt die
        // Differenz an.
        message: 'Der Skontoabzug lässt sich nicht strukturiert in den Zahlungsbedingungen (BT-20) ausweisen.',
        isViolated: (ctx) => {
            const skonto = ctx.invoice?.details?.skonto;
            if (!skonto) return false;
            return !formatSkontoTerms(skonto);
        },
    },
];

// ═══════════════════════════════════════════════════════════════════════════
// Hausregeln zur Leitweg-ID
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die KoSIT-Regeln verlangen nur IRGENDEINE Kaeuferreferenz (BR-DE-15). Ob eine
 * echte Leitweg-ID noetig ist, haengt daran, ob der Empfaenger ein oeffentlicher
 * Auftraggeber ist — das kann kein Validator wissen, unsere Stammdaten dagegen
 * schon. Deshalb zwei eigene Regeln: eine harte, wenn der Kunde als oeffentlich
 * gekennzeichnet ist, und eine Warnung, wenn nur der Name darauf hindeutet.
 */
const hasValidLeitwegId = (ctx: RuleContext): boolean =>
    !!ctx.leitwegId && LEITWEG_ID_PATTERN.test(ctx.leitwegId);

const isFlaggedPublicAuthority = (ctx: RuleContext): boolean | undefined =>
    readOptionalFlag(ctx.customer, 'isPublicAuthority') ??
    readOptionalFlag(ctx.customer, 'publicAuthority');

const LEITWEG_RULES: En16931Rule[] = [
    {
        code: 'BR-DE-LEITWEG-1',
        severity: 'error',
        field: 'details.buyerReference',
        message:
            'Der Auftraggeber ist als öffentlich gekennzeichnet. Dann ist eine gültige Leitweg-ID als Käuferreferenz Pflicht (BT-10).',
        isViolated: (ctx) => isFlaggedPublicAuthority(ctx) === true && !hasValidLeitwegId(ctx),
    },
    {
        code: 'BR-DE-LEITWEG-2',
        severity: 'warning',
        field: 'details.buyerReference',
        message: (ctx) =>
            `"${ctx.customer?.name ?? 'Der Empfänger'}" sieht nach einem öffentlichen Auftraggeber aus, es ist aber keine Leitweg-ID hinterlegt. Bitte prüfen.`,
        isViolated: (ctx) =>
            isFlaggedPublicAuthority(ctx) === undefined &&
            PUBLIC_AUTHORITY_HINT.test(ctx.customer?.name ?? '') &&
            !hasValidLeitwegId(ctx),
    },
];

// ═══════════════════════════════════════════════════════════════════════════
// Regelsatz und Einstieg
// ═══════════════════════════════════════════════════════════════════════════

export const EN16931_RULES: En16931Rule[] = [
    ...CORE_RULES,
    ...ARITHMETIC_RULES,
    ...STANDARD_RATE_RULES,
    ...GERMAN_RULES,
    ...LEITWEG_RULES,
];

/**
 * Prueft einen Beleg gegen EN 16931 und XRechnung.
 *
 * Gibt ALLE Verletzungen zurueck, nicht nur die erste: wer eine Rechnung
 * freigibt, will einmal nacharbeiten und nicht acht Mal hintereinander.
 */
export const validateEn16931 = (
    invoice: Invoice,
    customer: Customer | undefined,
    company: InvoiceCompanySnapshot | undefined,
    config?: InvoiceConfig,
): RuleViolation[] => {
    const ctx = buildRuleContext(invoice, customer, company, config);
    return EN16931_RULES.filter((rule) => rule.isViolated(ctx)).map((rule) => ({
        code: rule.code,
        severity: rule.severity,
        field: rule.field,
        message: typeof rule.message === 'function' ? rule.message(ctx) : rule.message,
    }));
};

/** Ob der Beleg so nicht als XRechnung versandt werden darf. */
export const hasEn16931Errors = (violations: RuleViolation[]): boolean =>
    violations.some((violation) => violation.severity === 'error');
